import { useEffect, useState } from "react";
import { ChevronDown } from "lucide-react";
import { api } from "@/lib/api";
import { useJourney } from "@/v2/V2Shell";

export function YearMap() {
  const { s } = useJourney();
  const [meta, setMeta] = useState(null);
  const [stars, setStars] = useState({});
  useEffect(() => {
    api.get("/v2/meta").then(({ data }) => setMeta(data)).catch(() => {});
    api.get("/v2/missions").then(({ data }) => setStars(data.complete.reduce((a, m) => ({ ...a, [m.phase]: (a[m.phase] || 0) + 1 }), {}))).catch(() => {});
  }, []);
  if (!meta) return null;
  let wk = 1;
  return (
    <section className="space-y-3" data-testid="year-map">
      <div>
        <h2 className="font-display text-2xl">Your year, mapped</h2>
        <p className="text-sm text-stone-600 mt-1">Each letter of CAMRYN is a chapter. Here's everything the year covers. Weeks are a guide: you move on when you're ready.</p>
      </div>
      {meta.phases.map((p, i) => {
        const from = wk; wk += p.weeks;
        const projects = Object.values(meta.projects).filter((pr) => pr.phase === p.key);
        const state = i < s.journey.phase_index ? "done" : i === s.journey.phase_index ? "current" : "upcoming";
        return (
          <details key={p.key} open={state === "current"} className="card group" data-testid={`year-map-${p.key}`}>
            <summary className="list-none cursor-pointer p-4 flex items-center gap-4">
              <span className="h-11 w-11 shrink-0 rounded-full grid place-items-center font-display text-xl text-white" style={{ background: p.color, opacity: state === "upcoming" ? 0.55 : 1 }}>{p.letter}</span>
              <span className="flex-1 min-w-0">
                <span className="block font-semibold">{p.name} <span className="font-normal text-xs text-stone-500">· weeks {from}–{wk - 1} · {p.season}</span>
                  {stars[p.key] > 0 && <span className="ml-1.5 text-xs font-semibold text-[#C86D51] whitespace-nowrap" data-testid={`year-map-stars-${p.key}`}>★ {stars[p.key]} mission{stars[p.key] > 1 ? "s" : ""}</span>}</span>
                <span className="block text-xs text-stone-600 truncate">{state === "current" ? "You are here · " : state === "done" ? "Completed · " : ""}{p.tagline}</span>
              </span>
              <ChevronDown className="h-4 w-4 text-stone-500 transition-transform group-open:rotate-180" />
            </summary>
            <div className="px-4 pb-5 pl-[4.75rem] text-sm">
              <ul className="space-y-1.5 list-disc pl-4 text-stone-700">{p.covers.map((c) => <li key={c}>{c}</li>)}</ul>
              {projects.length > 0 && <p className="mt-3 text-xs text-stone-600"><b>Guided projects:</b> {projects.map((pr) => `${pr.title} (${pr.weeks} wk${pr.opt_in ? ", opt-in" : ""})`).join(", ")}</p>}
              <p className="mt-2 text-xs text-stone-600"><b>Ready to move on when:</b> {p.markers.join(" · ")} (any 2 of 3)</p>
            </div>
          </details>
        );
      })}
      <div className="card p-5" data-testid="year-rhythm">
        <h3 className="font-display text-lg">The rhythm of your year</h3>
        <dl className="mt-3 space-y-3 text-sm">
          {meta.rhythm.map((r) => <div key={r.when}><dt className="font-semibold">{r.when}</dt><dd className="text-stone-600">{r.items.join(" · ")}</dd></div>)}
        </dl>
      </div>
    </section>
  );
}
